import { Link } from "react-router-dom";
import { ArrowRight, Check, CreditCard, FileText, User, Users } from "lucide-react";
import { Reveal, MaskLine } from "@/components/Reveal";

const PLANS = [
  {
    icon: User,
    name: "1 Child",
    price: "$12",
    unit: "per trip",
    blurb: "Morning transportation to school + afternoon transportation home.",
    featured: false,
  },
  {
    icon: Users,
    name: "2 Siblings",
    price: "$9",
    unit: "per trip per child",
    blurb: "Save when siblings ride together.",
    featured: false,
  },
  {
    icon: Users,
    name: "3 Siblings",
    price: "$7",
    unit: "per trip per child",
    blurb: "Our biggest family savings for families with three children riding together.",
    featured: false,
  },
];

const INCLUDES = [
  "Morning school transportation",
  "Afternoon transportation home",
  "Consistent scheduled rides",
  "Your child's reserved spot",
  "Family-friendly sibling pricing",
  "Direct parent communication",
  "Safe, reliable handoff procedures",
];

const RATES = [
  { label: "1 child", rate: "≈ $12/trip" },
  { label: "2 siblings", rate: "≈ $9/trip per child" },
  { label: "3 siblings", rate: "≈ $7/trip per child" },
];

const GOOD_TO_KNOW = [
  {
    icon: FileText,
    text: (
      <>
        A <strong className="text-ink">one-time $75 family registration fee</strong> with your first
        payment — it covers onboarding, route planning, account setup and required documentation.
      </>
    ),
  },
  {
    icon: Users,
    text: (
      <>
        Additional children added later may be subject to a{" "}
        <strong className="text-ink">$25 onboarding fee</strong> per child.
      </>
    ),
  },
  {
    icon: CreditCard,
    text: (
      <>
        <strong className="text-ink">Monthly plans are billed automatically</strong> by card — no
        invoices to track.
      </>
    ),
  },
];

export default function Pricing() {
  return (
    <div data-testid="pricing-page">
      <section className="mx-auto max-w-7xl px-4 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-terra">Services &amp; Pricing</p>
        <h1 className="mt-3 max-w-3xl font-serif text-4xl font-semibold leading-[1.12] tracking-tight text-ink sm:text-5xl">
          <MaskLine delay={0.1}>
            Simple, <em className="text-terra">Reliable</em>
          </MaskLine>
          <MaskLine delay={0.22}>School Transportation.</MaskLine>
        </h1>
        <span
          data-testid="pricing-from-rate-badge"
          className="mt-6 inline-flex items-center rounded-full border border-gold bg-gold/15 px-4 py-1.5 text-sm font-semibold text-ink"
        >
          From approximately $12 per trip
        </span>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink/70 sm:text-lg">
          A dependable school transportation option for North Edmonton families who need a safe,
          consistent ride to and from school.
        </p>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/70 sm:text-lg">
          Your child's spot is reserved for the school days you need — so you don't have to arrange a
          new ride every morning.
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-4 pt-16 sm:px-6 lg:px-8">
        <h2 className="max-w-2xl font-serif text-3xl font-semibold leading-[1.15] tracking-tight text-ink sm:text-4xl">
          <MaskLine delay={0.1}>One Ride. One Less Thing</MaskLine>
          <MaskLine delay={0.22}>
            To <em className="text-terra">worry about.</em>
          </MaskLine>
        </h2>
        <div className="mt-10 grid items-stretch gap-6 lg:grid-cols-3">
          {PLANS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08} className="h-full">
              <div
                data-testid={`pricing-tier-${i}`}
                className="relative flex h-full flex-col rounded-[2rem] border border-line bg-surface p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-terra/10"
              >
                <div className="flex items-center gap-4">
                  <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-blush text-terra">
                    <t.icon size={22} strokeWidth={2.2} />
                  </span>
                  <h2 className="font-serif text-2xl font-semibold text-ink">{t.name}</h2>
                </div>
                <div className="mt-6 flex flex-wrap items-baseline gap-1.5">
                  <span className="text-sm font-semibold text-ink/55">≈</span>
                  <span className="font-serif text-5xl font-semibold tracking-tight text-ink">{t.price}</span>
                  <span className="text-sm font-medium text-ink/55">{t.unit}</span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-ink/65">{t.blurb}</p>
                <p className="mt-6 flex-1 border-t border-line pt-4 text-[11px] font-bold uppercase tracking-[0.18em] text-ink/45">
                  Reserved monthly service
                </p>
                <Link
                  to="/check-availability"
                  data-testid={`pricing-check-availability-btn-${i}`}
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-terra px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white shadow-lg shadow-terra/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-terra-dark"
                >
                  Check Availability <ArrowRight size={16} strokeWidth={2.6} />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pt-16 sm:px-6 lg:px-8">
        <Reveal>
          <div
            data-testid="pricing-includes"
            className="rounded-[2rem] border border-line bg-surface/70 px-7 py-9 sm:px-10"
          >
            <h2 className="font-serif text-2xl font-semibold text-ink sm:text-3xl">
              What Your Monthly Service Includes
            </h2>
            <ul className="mt-7 grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
              {INCLUDES.map((inc) => (
                <li key={inc} className="flex items-start gap-2.5 text-sm">
                  <Check size={18} strokeWidth={3} className="mt-0.5 shrink-0 text-sage" />
                  <span className="font-medium text-ink/80">{inc}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 border-t border-line pt-6 font-serif text-xl font-semibold leading-snug text-ink sm:text-2xl">
              No daily booking. No scrambling for rides. No wondering <em className="text-terra">who is picking your child up.</em>
            </p>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 pt-14 sm:px-6 lg:px-8">
        <Reveal>
          <div
            data-testid="pricing-how-it-works"
            className="grid gap-10 rounded-[2rem] bg-ink px-7 py-10 sm:px-10 lg:grid-cols-2 lg:gap-12"
          >
            <div>
              <h2 className="font-serif text-3xl font-semibold tracking-tight text-cream sm:text-4xl">
                How The Pricing Works
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-cream/70 sm:text-base">
                Mama's School Rides operates as a monthly reserved transportation service, rather than a
                pay-as-you-go taxi service.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-cream/70 sm:text-base">
                The approximate per-trip rate simply shows you what your transportation costs work out to
                across the school month.
              </p>
              <p className="mt-6 font-serif text-lg font-semibold text-gold sm:text-xl">
                Your child's transportation is reserved for the month.
              </p>
            </div>
            <div className="flex flex-col justify-center rounded-3xl bg-white/5 p-6 sm:p-8">
              <ul className="space-y-4">
                {RATES.map((r) => (
                  <li key={r.label} className="flex items-center justify-between gap-4 border-b border-white/10 pb-4 text-sm last:border-0 last:pb-0">
                    <span className="font-medium text-cream/85">{r.label}</span>
                    <span className="font-serif text-lg font-semibold text-cream">{r.rate}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm leading-relaxed text-cream/60">
                Monthly payments keep your child's place reserved and allow us to plan consistent routes
                and schedules.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 pt-14 pb-6 sm:px-6 lg:px-8">
        <Reveal>
          <div
            data-testid="pricing-good-to-know"
            className="rounded-[2rem] border border-line bg-surface/70 px-7 py-7 sm:px-9"
          >
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-ink/50">Good to know</p>
            <div className="mt-4 grid gap-5 sm:grid-cols-3">
              {GOOD_TO_KNOW.map((g, i) => (
                <div key={i} className="flex items-start gap-3">
                  <g.icon size={18} className="mt-0.5 shrink-0 text-terra" />
                  <p className="text-sm leading-relaxed text-ink/65">{g.text}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <Reveal>
          <div data-testid="pricing-disclaimer" className="mt-2 flex items-start gap-4 rounded-3xl border border-line bg-blush/50 p-6 sm:p-7">
            <FileText className="mt-0.5 shrink-0 text-terra" size={22} />
            <p className="text-sm leading-relaxed text-ink/70">
              <strong className="text-ink">A quick, honest note on pricing:</strong> monthly fees reserve
              your child's seat on the route for the full month. Missed rides — sick days, family
              vacations, or schedule changes — are not automatically refunded or credited, since the seat
              is held for your child either way. Please refer to the{" "}
              <Link to="/policies/transportation-agreement" className="font-semibold text-terra-dark underline underline-offset-2">
                Parent Transportation Agreement
              </Link>{" "}
              and{" "}
              <Link to="/policies/cancellation-refund-policy" className="font-semibold text-terra-dark underline underline-offset-2">
                Cancellation &amp; Refund Policy
              </Link>{" "}
              for full details.
            </p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
